import { useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import {
  setNavigationAction,
  resetNavigationAction,
} from 'app/shared-components/store/headerSlice';

type BackButtonHeader = {
  title: string;
  type?: 'button';
  navigateTo: string;
  backToHistory?: boolean;
  // Screens that always go one level up (backToHistory: false) can replace their own
  // history entry, so repeatedly going down and back up does not pile up entries the
  // browser's own back button then has to walk through.
  replace?: boolean;
  // Return false to keep the user where they are — editors use this to confirm unsaved work
  // before the header's back button leaves. The screen then owns completing the navigation.
  onBeforeNavigate?: () => boolean;
};

type TitleHeader = {
  title: string;
  type: 'label';
};

type UseNavigationHeaderProps = BackButtonHeader | TitleHeader;

export default function useNavigationHeader(props: UseNavigationHeaderProps): void {
  const dispatch = useDispatch();
  const { title } = props;
  const isTitleOnly = props.type === 'label';
  const navigationCallback = useBackNavigation(isTitleOnly ? null : props);

  useEffect(() => {
    dispatch(
      setNavigationAction(
        isTitleOnly
          ? { title, type: 'label' }
          : { title, type: 'button', callback: navigationCallback }
      )
    );

    return () => {
      dispatch(resetNavigationAction());
    };
  }, [dispatch, title, isTitleOnly, navigationCallback]);
}

function useBackNavigation(header: BackButtonHeader | null): () => void {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateTo = header?.navigateTo;
  const backToHistory = header?.backToHistory ?? true;
  const replace = header?.replace ?? false;
  const onBeforeNavigate = header?.onBeforeNavigate;

  const previousPageIsLogin = isPreviousPageLogin();

  const canGoBack = useMemo(
    () => backToHistory && hasEarlierAppEntry(location.key) && !previousPageIsLogin,
    [backToHistory, location.key, previousPageIsLogin]
  );

  return useCallback(() => {
    if (onBeforeNavigate && !onBeforeNavigate()) return;
    if (canGoBack) {
      navigate(-1);
      return;
    }
    if (navigateTo) navigate(navigateTo, { replace });
  }, [canGoBack, navigate, navigateTo, replace, onBeforeNavigate]);
}

// A replace (tab switch, redirect on landing) gives the location a fresh key, so the key alone
// cannot tell a direct landing apart. The browser router keeps its entry index on
// history.state, and a replace keeps that index, so 0 means nothing in the app to go back to.
// Routers that do not write it (MemoryRouter in tests) fall back to the key.
function hasEarlierAppEntry(locationKey: string): boolean {
  const index: unknown = window.history.state?.idx;
  if (typeof index === 'number') return index > 0;
  return locationKey !== 'default';
}

function isPreviousPageLogin(): boolean {
  try {
    // Check if previous page was /login using browser's referrer
    const { referrer } = document;
    const previousPagePathname = referrer && new URL(referrer).pathname;
    return previousPagePathname === '/login';
  } catch (error) {
    return false;
  }
}
